import { useState, useEffect, useRef, useCallback } from "react";
import { Heart, Play, Pause, RotateCcw, Volume2, VolumeX, Pill, Timer, Zap, ZapOff, Flag, HeartPulse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { logIntervention } from "@/utils/interventionLogger";

function useMetronome(bpm = 110) {
  const [active, setActive] = useState(false);
  const ctxRef = useRef(null);
  const intervalRef = useRef(null);
  const start = useCallback(() => {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = ctxRef.current;
    const tick = () => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 800; g.gain.value = 0.3;
      o.start(ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
      o.stop(ctx.currentTime + 0.08);
    };
    tick();
    intervalRef.current = setInterval(tick, 60000 / bpm);
    setActive(true);
  }, [bpm]);
  const stop = useCallback(() => { clearInterval(intervalRef.current); setActive(false); }, []);
  const toggle = useCallback(() => { active ? stop() : start(); }, [active, start, stop]);
  useEffect(() => () => clearInterval(intervalRef.current), []);
  return { active, toggle };
}

function CycleTimer({ onShock, onCycleChange }) {
  const CPR_SEC = 120, CHECK_SEC = 10;
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState("cpr"); // "cpr" | "check"
  const [elapsed, setElapsed] = useState(0);
  const [checkElapsed, setCheckElapsed] = useState(0);
  const [cycle, setCycle] = useState(1);
  const [shocks, setShocks] = useState(0);
  const [log, setLog] = useState([]);
  const startRef = useRef(null);
  const checkStartRef = useRef(null);
  const animRef = useRef(null);
  const totalRef = useRef(0);

  const playAlert = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [0, 0.2, 0.4].forEach(d => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = 1200; g.gain.value = 0.5;
        o.start(ctx.currentTime + d);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + d + 0.15);
        o.stop(ctx.currentTime + d + 0.15);
      });
    } catch {}
  }, []);

  const startCheck = useCallback(() => {
    setPhase("check"); setCheckElapsed(0);
    checkStartRef.current = Date.now();
    playAlert();
  }, [playAlert]);

  const startNextCycle = useCallback(() => {
    setCycle(c => { const next = c + 1; onCycleChange?.(next); return next; });
    setPhase("cpr");
    setCheckElapsed(0); checkStartRef.current = null;
    setElapsed(0); startRef.current = Date.now();
  }, [onCycleChange]);

  const tick = useCallback(() => {
    if (phase === "cpr" && startRef.current) {
      const e = Math.floor((Date.now() - startRef.current) / 1000);
      totalRef.current += (e - elapsed > 0 ? e - elapsed : 0);
      setElapsed(e);
      if (e >= CPR_SEC) startCheck();
    } else if (phase === "check" && checkStartRef.current) {
      const ce = Math.floor((Date.now() - checkStartRef.current) / 1000);
      setCheckElapsed(ce);
      if (ce >= CHECK_SEC) startNextCycle();
    }
    animRef.current = requestAnimationFrame(tick);
  }, [phase, elapsed, startCheck, startNextCycle]);

  const start = () => {
    if (phase === "cpr") startRef.current = Date.now() - elapsed * 1000;
    else checkStartRef.current = Date.now() - checkElapsed * 1000;
    setRunning(true);
    animRef.current = requestAnimationFrame(tick);
  };
  const pause = () => { setRunning(false); if (animRef.current) cancelAnimationFrame(animRef.current); };
  const reset = () => {
    pause(); setElapsed(0); setCheckElapsed(0); setCycle(1); setShocks(0); setLog([]);
    setPhase("cpr"); startRef.current = null; checkStartRef.current = null; totalRef.current = 0;
    onCycleChange?.(1);
  };
  const getTimeStr = () => { const t = totalRef.current; return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`; };

  const handleShock = () => {
    const n = shocks + 1; setShocks(n); onShock?.(n);
    setLog(prev => [...prev, { type: "shock", cycle, time: getTimeStr(), num: n }]);
    startNextCycle();
  };
  const handleNoShock = () => {
    setLog(prev => [...prev, { type: "no-shock", cycle, time: getTimeStr() }]);
    if (phase === "check") startNextCycle();
  };

  useEffect(() => () => { if (animRef.current) cancelAnimationFrame(animRef.current); }, []);
  useEffect(() => {
    if (running) { if (animRef.current) cancelAnimationFrame(animRef.current); animRef.current = requestAnimationFrame(tick); }
  }, [phase, running, tick]);

  const isCheck = phase === "check";
  const remaining = isCheck ? Math.max(CHECK_SEC - checkElapsed, 0) : Math.max(CPR_SEC - elapsed, 0);
  const pct = isCheck ? (checkElapsed / CHECK_SEC) * 100 : (elapsed / CPR_SEC) * 100;
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0"), ss = String(remaining % 60).padStart(2, "0");
  const warn = !isCheck && remaining <= 30, crit = !isCheck && remaining <= 10;

  return (
    <div className={`rounded-lg border-2 p-3 transition-colors ${isCheck ? 'border-yellow-500 bg-yellow-500/10' : crit ? 'border-red-500 bg-red-500/10' : warn ? 'border-amber-500 bg-amber-500/5' : 'border-border'}`} data-testid="mini-cycle-timer">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold flex items-center gap-1"><Timer className="w-3.5 h-3.5" />{isCheck ? "Rhythm Check" : "CPR Cycle"}</span>
        <div className="flex items-center gap-1">
          {shocks > 0 && <Badge className="bg-red-500 text-white text-[10px] px-1.5">{shocks} shock{shocks !== 1 ? 's' : ''}</Badge>}
          <Badge variant="outline" className="text-[10px]">Cycle {cycle}</Badge>
        </div>
      </div>
      <div className="w-full h-2 bg-secondary rounded-full overflow-hidden mb-2">
        <div className={`h-full rounded-full ${isCheck ? 'bg-yellow-500' : crit ? 'bg-red-500' : warn ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-center mb-2">
        {isCheck && <p className="text-yellow-500 font-bold text-[10px] mb-0.5 animate-pulse">RHYTHM CHECK — ANALYZE RHYTHM</p>}
        <span className={`font-mono text-3xl font-bold ${isCheck ? 'text-yellow-500' : crit ? 'text-red-500 animate-pulse' : warn ? 'text-amber-500' : ''}`}>
          {isCheck ? `00:${String(remaining).padStart(2, "0")}` : `${mm}:${ss}`}
        </span>
        {!isCheck && crit && <p className="text-red-500 font-bold text-xs animate-pulse">PREPARE FOR RHYTHM CHECK</p>}
      </div>
      <div className="flex gap-2 justify-center mb-2">
        <Button size="sm" onClick={running ? pause : start} className="gap-1 h-7 text-xs" variant={running ? "outline" : "default"}>
          {running ? <><Pause className="w-3 h-3" />Pause</> : <><Play className="w-3 h-3" />{elapsed > 0 || checkElapsed > 0 ? 'Resume' : 'Start'}</>}
        </Button>
        <Button size="sm" variant="ghost" onClick={reset} className="h-7 text-xs gap-1"><RotateCcw className="w-3 h-3" /></Button>
      </div>
      {(running || isCheck) && (
        <div className="flex gap-2 justify-center mb-2">
          <Button size="sm" variant="destructive" onClick={handleShock} className={`gap-1 text-xs flex-1 ${isCheck ? 'h-9 text-sm' : 'h-7'}`} data-testid="mini-shock-btn">
            <Zap className={isCheck ? "w-4 h-4" : "w-3 h-3"} />Shock{shocks > 0 ? ` (${shocks})` : ''}
          </Button>
          <Button size="sm" variant="outline" onClick={handleNoShock} className={`gap-1 text-xs flex-1 ${isCheck ? 'h-9 text-sm' : 'h-7'}`} data-testid="mini-no-shock-btn">
            <ZapOff className={isCheck ? "w-4 h-4" : "w-3 h-3"} />No Shock
          </Button>
        </div>
      )}
      {log.length > 0 && (
        <div className="border rounded p-1.5 max-h-20 overflow-auto">
          {log.map((e, i) => (
            <div key={i} className="text-[10px] font-mono flex gap-1.5">
              <span className="text-muted-foreground">{e.time}</span>
              <span className="text-muted-foreground">C{e.cycle}</span>
              {e.type === "shock" ? <span className="text-red-400 font-semibold">Shock #{e.num}</span> : <span className="text-muted-foreground">No shock</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DrugTimerMini({ name, color, onDose }) {
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [doses, setDoses] = useState(0);
  const animRef = useRef(null);
  const tick = useCallback(() => {
    if (!startTime) return;
    setElapsed(Math.floor((Date.now() - startTime) / 1000));
    animRef.current = requestAnimationFrame(tick);
  }, [startTime]);
  useEffect(() => { if (startTime) animRef.current = requestAnimationFrame(tick); return () => cancelAnimationFrame(animRef.current); }, [startTime, tick]);
  const administer = () => { setStartTime(Date.now()); setElapsed(0); setDoses(p => { const next = p + 1; onDose?.(next); return next; }); };
  const reset = () => { cancelAnimationFrame(animRef.current); setStartTime(null); setElapsed(0); setDoses(0); onDose?.(0); };
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0"), ss = String(elapsed % 60).padStart(2, "0");
  const yellow = elapsed >= 180 && elapsed < 300, red = elapsed >= 300;
  return (
    <div className={`rounded-lg border-2 p-3 ${red ? 'border-red-500 bg-red-500/10' : yellow ? 'border-amber-500 bg-amber-500/5' : 'border-border'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold flex items-center gap-1"><Pill className={`w-3.5 h-3.5 ${color}`} />{name}</span>
        {doses > 0 && <Badge variant="secondary" className="text-[10px]">Dose {doses}</Badge>}
      </div>
      <div className="text-center mb-2">
        {startTime ? (
          <>
            <span className={`font-mono text-2xl font-bold ${red ? 'text-red-500 animate-pulse' : yellow ? 'text-amber-500' : ''}`}>{mm}:{ss}</span>
            {red && <p className="text-red-500 font-bold text-[10px] animate-pulse">NEXT DOSE DUE</p>}
            {yellow && !red && <p className="text-amber-500 text-[10px]">Prepare next dose</p>}
          </>
        ) : <span className="text-xl font-mono text-muted-foreground">--:--</span>}
      </div>
      <div className="flex gap-1 justify-center">
        <Button size="sm" onClick={administer} className="gap-1 h-7 text-xs" data-testid={`mini-${name.toLowerCase()}-administer`}><Pill className="w-3 h-3" />{doses === 0 ? "1st Dose" : "Next Dose"}</Button>
        {startTime && <Button size="sm" variant="ghost" onClick={reset} className="h-7 text-xs"><RotateCcw className="w-3 h-3" /></Button>}
      </div>
    </div>
  );
}

// ── Code Result (Mini) — resolution dispositions for the pop-out timer ──
function CodeResultMini({ stats, onResolved }) {
  const [result, setResult] = useState(null);

  const handleResult = (type) => {
    const wallTime = new Date().toLocaleTimeString();
    const codeDuration = Math.floor((Date.now() - stats.startTime) / 1000);
    const mm = Math.floor(codeDuration / 60);
    const ss = String(codeDuration % 60).padStart(2, "0");
    const elapsed = `${mm}:${ss}`;
    const labelMap = {
      "rosc-field": "ROSC — Field",
      "rosc-hospital": "ROSC — Hospital",
      "tor-field": "TOR — Field",
      "tor-hospital": "TOR — Hospital",
    };
    setResult({ type, time: wallTime, elapsed });
    // Comprehensive intervention log on resolution
    const summary = [
      `Outcome: ${labelMap[type] || type}`,
      `Wall time: ${wallTime}`,
      `Code duration: ${elapsed}`,
      `Cycles completed: ${Math.max(stats.cycles - 1, 0)}`,
      `Shocks delivered: ${stats.shocks}`,
      `Epinephrine doses: ${stats.epi}`,
      `Amiodarone doses: ${stats.amio}`,
    ].join(" · ");
    logIntervention({
      type: "intervention",
      name: `Code summary — ${labelMap[type] || type}`,
      notes: summary,
    }).catch(() => {});
    onResolved?.(type);
  };

  if (result) {
    const isROSC = result.type.startsWith("rosc");
    return (
      <div className={`rounded-lg border-2 p-3 text-center ${isROSC ? 'border-green-500 bg-green-500/10' : 'border-muted bg-muted/20'}`} data-testid="mini-code-result-final">
        <div className={`mb-1 ${isROSC ? 'text-green-400' : 'text-muted-foreground'}`}>
          {isROSC ? <HeartPulse className="w-7 h-7 mx-auto" /> : <Flag className="w-7 h-7 mx-auto" />}
        </div>
        <div className="font-heading font-bold text-sm">
          {result.type === "rosc-field" && "ROSC — Field"}
          {result.type === "rosc-hospital" && "ROSC — Hospital"}
          {result.type === "tor-field" && "TOR — Field"}
          {result.type === "tor-hospital" && "TOR — Hospital"}
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {result.time} · {result.elapsed} · {stats.shocks} shock{stats.shocks !== 1 ? 's' : ''} · Epi {stats.epi} · Amio {stats.amio}
        </p>
        <p className="text-[10px] text-emerald-400 mt-1">Logged to Intervention Tracker</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border p-3" data-testid="mini-code-result">
      <div className="flex items-center gap-2 mb-2">
        <Flag className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="font-heading font-semibold text-xs">Code Result</span>
        <span className="text-[10px] text-muted-foreground ml-auto">Logs full summary</span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <Button size="sm" onClick={() => handleResult("rosc-field")} className="gap-1 bg-green-600 hover:bg-green-700 text-white h-9 text-xs" data-testid="mini-result-rosc-field">
          <HeartPulse className="w-3.5 h-3.5" />ROSC — Field
        </Button>
        <Button size="sm" onClick={() => handleResult("rosc-hospital")} className="gap-1 bg-green-700 hover:bg-green-800 text-white h-9 text-xs" data-testid="mini-result-rosc-hospital">
          <HeartPulse className="w-3.5 h-3.5" />ROSC — Hospital
        </Button>
        <Button size="sm" onClick={() => handleResult("tor-field")} variant="outline" className="gap-1 h-9 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="mini-result-tor-field">
          <Flag className="w-3.5 h-3.5" />TOR — Field
        </Button>
        <Button size="sm" onClick={() => handleResult("tor-hospital")} variant="outline" className="gap-1 h-9 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="mini-result-tor-hospital">
          <Flag className="w-3.5 h-3.5" />TOR — Hospital
        </Button>
      </div>
    </div>
  );
}

export default function CodeTimerButton() {
  const [open, setOpen] = useState(false);
  const metronome = useMetronome(110);
  // Aggregate stats for comprehensive intervention log
  const [stats, setStats] = useState({ shocks: 0, cycles: 1, epi: 0, amio: 0, startTime: Date.now() });

  // Reset stats every time the dialog opens
  useEffect(() => {
    if (open) {
      setStats({ shocks: 0, cycles: 1, epi: 0, amio: 0, startTime: Date.now() });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 text-red-400 border-red-500/30 hover:bg-red-500/10" data-testid="code-timer-trigger">
          <Heart className="w-3.5 h-3.5" />
          Code Timer
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500" /> Code Timer
          </DialogTitle>
        </DialogHeader>

        {/* Metronome */}
        <div className="flex justify-center">
          <Button
            onClick={metronome.toggle}
            variant={metronome.active ? "default" : "outline"}
            className={`gap-2 w-full ${metronome.active ? 'animate-pulse' : ''}`}
            data-testid="mini-metronome-toggle"
          >
            {metronome.active ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            110 BPM Metronome
            {metronome.active && <Badge variant="secondary" className="text-[10px]">ON</Badge>}
          </Button>
        </div>

        <CycleTimer
          onShock={(n) => setStats((s) => ({ ...s, shocks: n }))}
          onCycleChange={(n) => setStats((s) => ({ ...s, cycles: n }))}
        />

        <div className="grid grid-cols-2 gap-3">
          <DrugTimerMini name="Epinephrine" color="text-green-500" onDose={(n) => setStats((s) => ({ ...s, epi: n }))} />
          <DrugTimerMini name="Amiodarone" color="text-blue-500" onDose={(n) => setStats((s) => ({ ...s, amio: n }))} />
        </div>

        {/* Resolution dispositions — auto-log full code summary on selection */}
        <CodeResultMini stats={stats} />

        <p className="text-[10px] text-muted-foreground text-center">
          CPR: 2-min cycles. Epi q3-5min. Amiodarone after 3rd shock.
        </p>
      </DialogContent>
    </Dialog>
  );
}
